import { useEffect, useState } from 'react';
import { Avatar } from '../Avatar';
import { EmptyState } from '../EmptyState';
import { useToast } from '../Toast';
import { Button } from '../ui';
import {
  fetchMembers,
  setAdminPermission,
  type MemberRow,
} from '../../lib/members-admin-queries';

// ===========================================================================
// Admins panel - developer-only. Lists admins and toggles per-admin
// permissions. The first permission is can_impersonate, which lets an admin
// use the impersonation flow that's otherwise developer-only.
// ===========================================================================
export function AdminsPanel({ active, meId }: { active: boolean; meId: string }) {
  const toast = useToast();
  const [admins, setAdmins] = useState<MemberRow[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savingId, setSavingId] = useState<string | null>(null);

  async function refresh() {
    setLoading(true);
    setError(null);
    try {
      const all = await fetchMembers();
      // Admins (and developers other than yourself) can hold permissions.
      setAdmins(all.filter((m) => !!m.user_id && (m.role === 'admin' || m.role === 'developer')));
    } catch (err: any) {
      setError(err?.message ?? 'Failed to load admins.');
    } finally {
      setLoading(false);
      setLoaded(true);
    }
  }

  useEffect(() => {
    if (active && !loaded) void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  async function togglePermission(m: MemberRow, key: string, value: boolean) {
    if (!m.user_id) return;
    setSavingId(m.user_id);
    try {
      await setAdminPermission({ userId: m.user_id, key, value });
      toast.success(
        value
          ? `${m.display_name || m.email} can now impersonate members.`
          : `Impersonation disabled for ${m.display_name || m.email}.`,
      );
      await refresh();
    } catch (err: any) {
      toast.error(err?.message ?? 'Update failed.');
    } finally {
      setSavingId(null);
    }
  }

  if (!loaded && loading) {
    return (
      <>
        <div className="admin__panel-head"><h2>Admins</h2></div>
        <p className="admin__note">Loading…</p>
      </>
    );
  }

  return (
    <>
      <div className="admin__panel-head">
        <h2>Admins</h2>
        <span className="admin__count">{admins.length}</span>
      </div>

      {error && (
        <div className="admin__error">
          <p className="kicker">Problem</p>
          <p>{error}</p>
        </div>
      )}

      <p className="admin__note">
        Grant per-admin permissions. <strong>Impersonation</strong> lets an admin sign in as any
        member to see their view, every session is written to <code>impersonation_log</code>.
        Developers always have this. Only developers can change these flags.
      </p>

      {admins.length === 0 ? (
        <EmptyState
          tone="compact"
          align="start"
          eyebrow="No admins"
          headline="No admins yet."
          body="Promote a member to Admin from the Members tab first."
        />
      ) : (
        <div className="admin__table-wrap">
          <table className="admin__table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Role</th>
                <th>Impersonation</th>
              </tr>
            </thead>
            <tbody>
              {admins.map((m) => {
                const isDev = m.role === 'developer';
                const canImp = isDev || m.permissions?.can_impersonate === true;
                const isSelf = m.user_id === meId;
                return (
                  <tr key={m.user_id}>
                    <td className="admin__cell-name">
                      <span className="admin__person">
                        <Avatar name={m.display_name || m.email} size="sm" founding={m.is_founding_member} />
                        <span>
                          {m.display_name || ' - '}
                          {isSelf && <span className="pill pill--you" style={{ marginLeft: 6 }}>you</span>}
                          {m.handle && (
                            <span style={{ color: 'var(--text-soft)', display: 'block', fontSize: '0.85em' }}>
                              @{m.handle}
                            </span>
                          )}
                        </span>
                      </span>
                    </td>
                    <td className="admin__cell-email"><code title={m.email}>{m.email}</code></td>
                    <td><span className="pill pill--role">{m.role === 'developer' ? 'Developer' : 'Admin'}</span></td>
                    <td>
                      {isDev ? (
                        <span style={{ color: 'var(--text-soft)', fontSize: '0.9em' }} title="Developers always can.">
                          Always
                        </span>
                      ) : (
                        <Button
                          type="button"
                          size="sm"
                          variant={canImp ? 'secondary' : 'ghost'}
                          style={{ fontSize: '0.85em' }}
                          onClick={() => togglePermission(m, 'can_impersonate', !canImp)}
                          disabled={savingId === m.user_id}
                          aria-pressed={canImp}
                        >
                          {savingId === m.user_id ? '…' : canImp ? '✓ Enabled' : 'Enable'}
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
